import { useRef, useState } from "react";
import { useTheme } from "../../context/ThemeContext";

/**
 * ChatInputBar — sticky bottom bar.
 *
 * Props:
 *  onSend               fn(text)
 *  onComplete           fn()
 *  showComplete         bool
 *  saving               bool
 *  aiLoading            bool
 *  unansweredCount      number
 *  firstUnansweredId    string|null
 *  onJumpFirstUnanswered fn()
 *  micSupported         bool
 *  micListening         bool
 *  onMicClick           fn()
 *  voiceEnabled         bool
 *  onVoiceToggle        fn()
 */
export default function ChatInputBar({
  onSend,
  onComplete,
  showComplete = false,
  saving = false,
  aiLoading = false,
  unansweredCount = 0,
  firstUnansweredId = null,
  onJumpFirstUnanswered,
  micSupported = false,
  micListening = false,
  onMicClick,
  voiceEnabled = false,
  onVoiceToggle,
  inline = false,   // when true: normal flow (no fixed positioning)
}) {
  const { theme } = useTheme();
  const dark = theme === "dark";
  const [text, setText] = useState("");
  const textareaRef = useRef(null);

  const canSend = text.trim().length > 0 && !aiLoading;

  function handleSend() {
    const t = text.trim();
    if (!t || aiLoading) return;
    onSend(t);
    setText("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }

  function handleInput(e) {
    setText(e.target.value);
    const el = textareaRef.current;
    if (el) { el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 110)}px`; }
  }

  const inputBg = dark ? "rgba(255,255,255,0.055)" : "#f8fafc";
  const inputBorder = dark ? "rgba(255,255,255,0.11)" : "rgba(0,79,115,0.14)";

  return (
    <div
      className={`chat-input-bar ${inline ? "" : "chat-input-bar--fixed"}`}
      style={inline ? {
        flexShrink: 0,
        background: dark ? "rgba(4,8,15,0.55)" : "rgba(248,250,252,0.92)",
        backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
        borderTop: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,79,115,0.08)"}`,
      } : {
        position: "fixed",
        bottom: 0, left: 0, right: 0,
        zIndex: 50,
        background: "transparent",
      }}
    >
      <div style={{ maxWidth: 760, margin: "0 auto" }}>

        {/* hint row */}
        {unansweredCount > 0 && firstUnansweredId && !showComplete && (
          <div style={{ marginBottom: 7, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={onJumpFirstUnanswered}
              style={{ fontSize: 11, fontWeight: 600, color: dark ? "#38bdf8" : "#004f73", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2, padding: 0 }}
            >
              ↑ Jump to first unanswered
            </button>
            <span style={{ fontSize: 11, color: dark ? "rgba(255,255,255,0.28)" : "#94a3b8" }}>
              {unansweredCount} remaining
            </span>
          </div>
        )}

        {/* input row */}
        <div className="chat-input-row">

          {/* mic button */}
          {micSupported && (
            <button
              type="button"
              className="chat-input-mic"
              onClick={onMicClick}
              title={micListening ? "Listening… click to stop" : "Speak a question or rating"}
              style={{
                width: 40, height: 40, borderRadius: "50%",
                flexShrink: 0, flexBasis: 40,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: micListening
                  ? "rgba(239,68,68,0.15)"
                  : dark ? "rgba(255,255,255,0.07)" : "rgba(0,79,115,0.07)",
                border: micListening
                  ? "1.5px solid rgba(239,68,68,0.5)"
                  : dark ? "1.5px solid rgba(255,255,255,0.12)" : "1.5px solid rgba(0,79,115,0.15)",
                cursor: "pointer",
                transition: "all 0.15s",
                animation: micListening ? "chat-pulse-mic 1.2s ease-in-out infinite" : "none",
              }}
            >
              {micListening ? (
                /* waveform / stop icon */
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(239,68,68,0.9)" strokeWidth={2.5}>
                  <rect x="6" y="6" width="12" height="12" rx="2" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={dark ? "rgba(255,255,255,0.55)" : "rgba(0,79,115,0.6)"} strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" />
                </svg>
              )}
            </button>
          )}

          {/* text input */}
          <div
            className="chat-input-field-wrap"
            style={{
              flex: 1,
              display: "flex", alignItems: "flex-end",
              background: inputBg,
              border: `1.5px solid ${inputBorder}`,
              borderRadius: 24,
              padding: "8px 12px 8px 16px",
              transition: "border-color 0.15s",
              minWidth: 0,
            }}
          >
            <textarea
              ref={textareaRef}
              value={text}
              onChange={handleInput}
              onKeyDown={handleKey}
              rows={1}
              placeholder={
                aiLoading ? "Guide is responding…" :
                micListening ? "Listening…" :
                "Ask about any term or statement…"
              }
              disabled={aiLoading || micListening}
              style={{
                flex: 1, background: "transparent", border: "none", outline: "none",
                resize: "none", fontSize: 14, lineHeight: 1.5,
                color: dark ? "rgba(255,255,255,0.88)" : "#0f172a",
                maxHeight: 110, overflowY: "auto", padding: 0,
              }}
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              style={{
                width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: canSend
                  ? dark ? "#004f73" : "#004f73"
                  : dark ? "rgba(255,255,255,0.07)" : "rgba(0,79,115,0.07)",
                border: "none", cursor: canSend ? "pointer" : "not-allowed",
                transition: "all 0.15s", marginLeft: 4,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke={canSend ? "#fff" : dark ? "rgba(255,255,255,0.25)" : "rgba(0,79,115,0.25)"}
                strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V5m-7 7l7-7 7 7" />
              </svg>
            </button>
          </div>

          {/* voice toggle */}
          {typeof onVoiceToggle === "function" && (
            <button
              type="button"
              className="chat-input-voice"
              onClick={onVoiceToggle}
              title={voiceEnabled ? "AI voice on — click to mute" : "AI voice off — click to enable"}
              style={{
                width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: voiceEnabled
                  ? dark ? "rgba(0,79,115,0.35)" : "rgba(0,79,115,0.10)"
                  : dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                border: voiceEnabled
                  ? dark ? "1.5px solid rgba(56,189,248,0.4)" : "1.5px solid rgba(0,79,115,0.35)"
                  : dark ? "1.5px solid rgba(255,255,255,0.09)" : "1.5px solid rgba(0,0,0,0.08)",
                cursor: "pointer", transition: "all 0.15s",
              }}
            >
              {voiceEnabled ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={dark ? "#38bdf8" : "#004f73"} strokeWidth={2}>
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <path strokeLinecap="round" d="M15.54 8.46a5 5 0 0 1 0 7.07M19.07 4.93a10 10 0 0 1 0 14.14" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={dark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.3)"} strokeWidth={2}>
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </svg>
              )}
            </button>
          )}

          {/* Complete CTA */}
          {showComplete && (
            <button
              type="button"
              className="chat-input-complete-btn"
              onClick={onComplete}
              disabled={saving}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                borderRadius: 999, padding: "10px 20px",
                fontSize: 13, fontWeight: 700,
                cursor: saving ? "not-allowed" : "pointer",
                border: "none",
                background: "linear-gradient(105deg,#004f73 0%,#105a7c 45%,#1a7fa8 100%)",
                backgroundSize: "200% auto",
                animation: "chat-shimmer 3s linear infinite",
                color: "#fff",
                boxShadow: "0 0 20px rgba(0,79,115,0.5)",
                opacity: saving ? 0.5 : 1,
                transition: "opacity 0.2s",
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="#fff">
                <path d="M12 2l2.09 6.26L20 10l-5.91 1.74L12 18l-2.09-6.26L4 10l5.91-1.74z" />
              </svg>
              {saving ? "Saving…" : "Complete"}
            </button>
          )}
        </div>

        <p style={{ fontSize: 11, color: dark ? "rgba(255,255,255,0.18)" : "#cbd5e1", marginTop: 7, textAlign: "center" }}>
          The guide explains context — it won't influence your ratings.
        </p>
      </div>
    </div>
  );
}
